import { AppShell, Group, Menu, Text, UnstyledButton } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { logout } from '@/shared/api/auth';
import { Icon } from '@/shared/components/Icon';
import { NewVersionBar } from '@/shared/components/NewVersionBar';
import { PageOutlet } from '@/shared/components/PageOutlet';
import { leaveSignedOut, sessionQueryOptions } from '@/shared/session/session';
import { useSessionExpiry } from '@/shared/session/useSessionExpiry';

import classes from './PlatformLayout.module.css';
import { PLATFORM_NAV_ITEMS } from './platformNavigation';

/**
 * `/platform/*` (13 §3.4, `AM-134` point 11): the area a Platform Administrator sees instead of a
 * tenant's console. Guarded by the router on `session.platform.isPlatformAdmin` alone - `GET /me`
 * needs a tenant context (`RequireTenant`) that an administrator with no membership never has
 * (`AM-134` point 7; `platform@serviceledger.local` is exactly that case), so this shell never
 * calls it. The menu itself hides an entry whose main `GET` the session's platform keys do not
 * allow (08 §6.2) - the route still shows the console's own "sin acceso" page if reached by URL.
 */
export function PlatformLayout() {
  const { t } = useTranslation(['platform', 'auth']);
  const session = useQuery(sessionQueryOptions());
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  useSessionExpiry();
  const platformPermissions = new Set(session.data?.platform.permissions ?? []);
  const visibleNavItems = PLATFORM_NAV_ITEMS.filter(
    (item) => item.permission === undefined || platformPermissions.has(item.permission),
  );
  // A console only exists for a session with a tenant to enter: a membership of its own, or the
  // tenant of a support session. Without either, `/` would only send the administrator back here.
  const hasConsole =
    (session.data?.memberships.length ?? 0) > 0 ||
    (session.data?.activeTenant !== null && session.data?.activeTenant !== undefined);

  const signOut = async () => {
    try {
      await logout();
    } finally {
      await leaveSignedOut(queryClient, () => navigate({ to: '/login' }));
    }
  };

  return (
    <AppShell header={{ height: 48 }} navbar={{ width: 224, breakpoint: 'sm' }} padding="lg">
      <AppShell.Header className={classes.header}>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Text className={classes.band}>{t('platform:shell.band')}</Text>
          <Menu position="bottom-end" width={240}>
            <Menu.Target>
              <UnstyledButton
                className={classes.userButton}
                aria-label={t('platform:shell.userMenu')}
              >
                <Group gap={8} wrap="nowrap">
                  <Icon name="user" size={16} />
                  <Text size="sm" visibleFrom="sm">
                    {session.data?.identity.email ?? ''}
                  </Text>
                  <Icon name="chevronDown" size={14} />
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              {hasConsole ? (
                <Menu.Item
                  component={Link}
                  to="/"
                  leftSection={<Icon name="arrowLeft" size={16} />}
                >
                  {t('platform:shell.backToConsole')}
                </Menu.Item>
              ) : null}
              <Menu.Item
                component={Link}
                to="/platform/account/security"
                leftSection={<Icon name="shield" size={16} />}
              >
                {t('auth:menu.security')}
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                leftSection={<Icon name="logout" size={16} />}
                onClick={() => void signOut()}
              >
                {t('auth:menu.signOut')}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar className={classes.navbar} p={0}>
        <nav aria-label={t('platform:shell.nav')}>
          {visibleNavItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={classes.item}
              activeProps={{ className: `${classes.item} ${classes.active}` }}
              activeOptions={{ exact: item.to === '/platform' }}
            >
              <Icon name={item.icon} size={16} />
              <span>{t(item.labelKey)}</span>
            </Link>
          ))}
        </nav>
      </AppShell.Navbar>

      <AppShell.Main className={classes.main}>
        <NewVersionBar />
        <PageOutlet />
      </AppShell.Main>
    </AppShell>
  );
}
